(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const leafColors = ['#d96029', '#e6a638', '#9f3f2f', '#bd6d26', '#7c3b32'];

  function initialize(section) {
    if (section.dataset.floatingObjectsReady === 'true') return;
    section.dataset.floatingObjectsReady = 'true';

    const canvas = section.querySelector('[data-floating-objects]');
    const effect = section.dataset.floatingObjects;
    if (!canvas || effect === 'none' || reducedMotion.matches) return;

    const context = canvas.getContext('2d');
    const pointer = { x: -1000, y: -1000, active: false };
    let width = 0;
    let height = 0;
    let pixelRatio = 1;
    let particles = [];
    let previousTime = performance.now();
    let previousScroll = window.scrollY;
    let scrollForce = 0;
    let scrollOpacity = 1;
    let targetScrollOpacity = 1;

    function updateScrollOpacity() {
      const fadeDistance = Math.max(320, height);
      targetScrollOpacity = Math.max(0, Math.min(1, 1 - window.scrollY / fadeDistance));
    }

    function resetParticle(particle, randomY = false) {
      particle.x = Math.random() * width;
      particle.y = randomY ? Math.random() * height : -40 - Math.random() * 140;
      particle.size = 10 + Math.random() * 13;
      particle.vx = Math.random() * 0.3 - 0.15;
      particle.vy = 0.2 + Math.random() * 0.36;
      particle.rotation = Math.random() * Math.PI * 2;
      particle.spin = (Math.random() - 0.5) * 0.018;
      particle.phase = Math.random() * Math.PI * 2;
      particle.depth = 0.55 + Math.random() * 0.75;
      particle.color = leafColors[Math.floor(Math.random() * leafColors.length)];
      particle.shape = Math.random() > 0.45 ? 'maple' : 'simple';
      particle.icon = ['🍗', '🥧', '🌽', '●'][Math.floor(Math.random() * 4)];
    }

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      const configuredDensity = Number(section.dataset.floatingDensity) || 16;
      const responsiveDensity = Math.round(configuredDensity * Math.min(1, width / 1200));
      const particleCount = Math.max(6, responsiveDensity);
      particles = Array.from({ length: particleCount }, (_, index) => particles[index] || {});
      particles.forEach((particle) => {
        if (particle.x == null) resetParticle(particle, true);
      });
      updateScrollOpacity();
    }

    function drawLeaf(particle) {
      const size = particle.size;
      context.fillStyle = particle.color;
      context.strokeStyle = particle.color;
      context.lineWidth = 1.4;
      context.beginPath();

      if (particle.shape === 'maple') {
        context.moveTo(0, -size);
        context.lineTo(size * 0.25, -size * 0.45);
        context.lineTo(size * 0.72, -size * 0.67);
        context.lineTo(size * 0.57, -size * 0.13);
        context.lineTo(size, 0);
        context.lineTo(size * 0.44, size * 0.28);
        context.lineTo(size * 0.48, size * 0.75);
        context.lineTo(0, size * 0.45);
        context.lineTo(-size * 0.48, size * 0.75);
        context.lineTo(-size * 0.44, size * 0.28);
        context.lineTo(-size, 0);
        context.lineTo(-size * 0.57, -size * 0.13);
        context.lineTo(-size * 0.72, -size * 0.67);
        context.lineTo(-size * 0.25, -size * 0.45);
      } else {
        context.moveTo(0, -size);
        context.bezierCurveTo(size * 0.9, -size * 0.55, size * 0.85, size * 0.55, 0, size);
        context.bezierCurveTo(-size * 0.85, size * 0.55, -size * 0.9, -size * 0.55, 0, -size);
      }

      context.closePath();
      context.fill();

      /* Keep the stem tied to the leaf's scroll opacity while preventing
         the overlapping stroke from appearing darker than the leaf body. */
      context.globalAlpha *= 0.25;
      context.beginPath();
      context.moveTo(0, -size * 0.75);
      context.lineTo(0, size * 1.22);
      context.stroke();
    }

    function drawParticle(particle) {
      context.save();
      context.translate(particle.x, particle.y);
      context.rotate(particle.rotation);
      context.scale(particle.depth, particle.depth);
      context.globalAlpha = effect === 'leaves' ? 0.82 * scrollOpacity : 0.82;

      if (effect === 'snow') {
        context.fillStyle = '#fff';
        context.font = `${particle.size * 1.6}px serif`;
        context.fillText('❄', -particle.size, -particle.size);
      } else if (effect === 'thanksgiving') {
        context.font = `${particle.size * 1.7}px serif`;
        context.fillText(particle.icon, -particle.size, -particle.size);
      } else {
        drawLeaf(particle);
      }

      context.restore();
    }

    function animate(time) {
      const delta = Math.min((time - previousTime) / 16.67, 2);
      previousTime = time;
      scrollForce *= 0.88;
      scrollOpacity += (targetScrollOpacity - scrollOpacity) * 0.12 * delta;
      context.clearRect(0, 0, width, height);

      particles.forEach((particle) => {
        const dx = particle.x - pointer.x;
        const dy = particle.y - pointer.y;
        const distance = Math.hypot(dx, dy);

        if (pointer.active && distance < 145 && distance > 0) {
          const force = Math.pow(1 - distance / 145, 2) * 1.9;
          particle.vx += (dx / distance) * force;
          particle.vy += (dy / distance) * force;
          particle.spin += (dx > 0 ? 1 : -1) * force * 0.004;
        }

        const breeze = Math.sin(time * 0.00055 + particle.phase) * 0.018;
        particle.vx += breeze * delta;
        particle.vx *= Math.pow(0.985, delta);
        particle.vy *= Math.pow(0.992, delta);
        particle.vy += 0.005 * delta;
        particle.spin *= Math.pow(0.995, delta);
        particle.x += (particle.vx + scrollForce * 0.015 * particle.depth) * delta;
        particle.y += (particle.vy + Math.abs(scrollForce) * 0.035 * particle.depth) * delta;
        particle.rotation += particle.spin * delta;

        if (particle.y > height + 60 || particle.x < -80 || particle.x > width + 80) {
          resetParticle(particle);
        }

        drawParticle(particle);
      });

      window.requestAnimationFrame(animate);
    }

    window.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.active = true;
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', () => { pointer.active = false; });
    window.addEventListener('blur', () => { pointer.active = false; });
    window.addEventListener('scroll', () => {
      scrollForce = Math.max(-30, Math.min(30, window.scrollY - previousScroll));
      if (effect === 'leaves') updateScrollOpacity();
      previousScroll = window.scrollY;
    }, { passive: true });
    window.addEventListener('resize', resize, { passive: true });

    resize();
    window.requestAnimationFrame(animate);
  }

  document.querySelectorAll('[data-campaign-builder]').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-campaign-builder]').forEach(initialize);
  });
})();
